"use client";

import { usePathname } from "next/navigation";
import Logo from "./Logo";
import styles from "./Footer.module.css";
import { ArrowUp } from "lucide-react";

// SVG Brand Icons for high-fidelity social squares matching original assets
const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 8H7v3h2v9h3v-9h3.6l.4-3H12V6c0-.9.1-1.1 1-1.1h1.8V2H12c-2.7 0-3 1.6-3 3.8V8z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);

const LinkedinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
  </svg>
);

const YoutubeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
  </svg>
);

export default function Footer() {
  const pathname = usePathname();
  const isHome = pathname === "/";

  const getLinkHref = (hash: string) => {
    return isHome ? hash : `/${hash}`;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className={styles.footer}>
      <div className={`${styles.container} container`}>
        
        {/* Main Grid: 5 columns */}
        <div className={styles.grid}>
          
          {/* Column 1: Brand details and social sharing card squares */}
          <div className={`${styles.column} ${styles.brandCol}`}>
            <Logo />
            <p className={styles.brandDesc}>
              The all-in-one hotel management software to manage your hotel operations, website, bookings, restaurant and more.
            </p>
            <div className={styles.socials}>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noreferrer" 
                className={styles.socialCard} 
                aria-label="Facebook"
                id="footer-social-facebook"
              >
                <FacebookIcon />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noreferrer" 
                className={styles.socialCard} 
                aria-label="Instagram"
                id="footer-social-instagram"
              >
                <InstagramIcon />
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noreferrer" 
                className={styles.socialCard} 
                aria-label="LinkedIn"
                id="footer-social-linkedin"
              >
                <LinkedinIcon />
              </a>
              <a 
                href="https://youtube.com" 
                target="_blank" 
                rel="noreferrer" 
                className={styles.socialCard} 
                aria-label="YouTube"
                id="footer-social-youtube"
              >
                <YoutubeIcon />
              </a>
            </div>
          </div>

          {/* Column 2: Product */}
          <div className={styles.column}>
            <h3 className={styles.title}>PRODUCT</h3>
            <ul className={styles.links}>
              <li><a href="/features">Features</a></li>
              <li><a href="/modules">Modules</a></li>
              <li><a href="/pricing">Pricing</a></li>
              <li><a href="/blog">Blog</a></li>
            </ul>
          </div>

          {/* Column 3: Company */}
          <div className={styles.column}>
            <h3 className={styles.title}>COMPANY</h3>
            <ul className={styles.links}>
              <li><a href="/about">About Us</a></li>
              <li><a href={getLinkHref("#careers")}>Careers</a></li>
              <li><a href="/contact">Contact Us</a></li>
            </ul>
          </div>

          {/* Column 4: Legal */}
          <div className={styles.column}>
            <h3 className={styles.title}>LEGAL</h3>
            <ul className={styles.links}>
              <li><a href="/privacy-policy">Privacy Policy</a></li>
              <li><a href="/terms-conditions">Terms & Conditions</a></li>
            </ul>
          </div>

          {/* Column 5: Contact details with Indian Noida address */}
          <div className={styles.column}>
            <h3 className={styles.title}>CONTACT</h3>
            <div className={styles.contactInfo}>
              <p className={styles.contactLabel}>Address:</p>
              <p className={styles.contactValue}>
                Office A-125, Sector 16A, Noida, <br />
                Uttar Pradesh, India
              </p>
              
              <p className={styles.contactLabel} style={{ marginTop: "16px" }}>Phone:</p>
              <p className={styles.contactValue}>
                <a href="tel:+919599287096" className={styles.phoneLink}>
                  +91 9599287096
                </a>
              </p>
            </div>
          </div>

        </div>

        {/* Horizontal Divider */}
        <hr className={styles.divider} />

        {/* Bottom Bar: Copyright on left, Back to Top on right */}
        {/* Bottom Bar: Copyright on left, Links & Back to Top on right */}
        <div className={styles.bottom}>
          <p className={styles.copyright}>
            © 2026 Moteligo. All rights reserved. <span className={styles.heart}>❤️</span> Crafted by <a href="https://scriza.in" target="_blank" rel="noreferrer" className={styles.scrizaLink}>Scriza</a>
          </p>
          <div className={styles.bottomRight}>
            <div className={styles.bottomLinks}>
              <a href="/privacy-policy" className={styles.bottomLink}>Privacy</a>
              <a href="/terms-conditions" className={styles.bottomLink}>Terms</a>
              <a href="/refund-policy" className={styles.bottomLink}>Refund Policy</a>
              <a href="/cookie-policy" className={styles.bottomLink}>Cookies</a>
            </div>
            <button 
              className={styles.scrollTopBtn} 
              onClick={scrollToTop}
              aria-label="Scroll to top"
              id="footer-scroll-top"
            >
              Back to Top <ArrowUp size={14} />
            </button>
          </div>
        </div>

      </div>
    </footer>
  );
}
